import { Injectable, Injector } from "@angular/core";
import { FeatureName } from "@common/ADAPT.Common.Model/embed/feature-name.enum";
import { FeaturePermissionName } from "@common/ADAPT.Common.Model/embed/feature-permission-name.enum";
import { IEntityWithOptionalTeam } from "@common/ADAPT.Common.Model/organisation/team-entity.interface";
import { Person } from "@common/ADAPT.Common.Model/person/person";
import { BaseService } from "@common/service/base.service";
import { AuthorisationService } from "@org-common/lib/authorisation/authorisation.service";
import { FeaturesService } from "@org-common/lib/features/features.service";
import { CommonTeamsService } from "@org-common/lib/teams/common-teams.service";
import { from } from "rxjs";
import { ISurveyAuthService } from "../survey-auth.service.interface";

@Injectable({
    providedIn: "root",
})
export class TeamAssessmentAuthService extends BaseService implements ISurveyAuthService {
    public static readonly EditTeamAssessment = "editTeamAssessment";
    public static readonly ReadTeamAssessment = "readTeamAssessment";
    public static readonly ReadAnyTeamAssessment = "readAnyTeamAssessment";

    public constructor(
        injector: Injector,
        private authService: AuthorisationService,
        private featuresService: FeaturesService,
    ) {
        super(injector);
    }

    public static registerAccessVerifiers(authorisationService: AuthorisationService) {
        authorisationService.registerAccessVerifier(
            TeamAssessmentAuthService.EditTeamAssessment,
            {
                requirePermissions: [
                    FeaturePermissionName.CulturalNetworkTeamAssessmentEdit,
                ],
            },
        );
        authorisationService.registerAccessVerifier(
            TeamAssessmentAuthService.ReadTeamAssessment,
            {
                requirePermissions: [
                    FeaturePermissionName.CulturalNetworkTeamAssessmentEdit,
                    FeaturePermissionName.CulturalNetworkTeamAssessmentRead,
                ],
            },
        );
        authorisationService.registerAccessVerifier(
            TeamAssessmentAuthService.ReadAnyTeamAssessment,
            {
                invokeToGetAccessVerifier: (injector) =>
                    getVerifyAccessToReadAnyTeamAssessment(
                        injector.get(CommonTeamsService),
                    ),
            },
        );

        function getVerifyAccessToReadAnyTeamAssessment(teamsService: CommonTeamsService) {
            return async (_currentPerson: Person) => {
                const personHasGlobalTeamAssessmentRead = await authorisationService.promiseToGetHasAccess(TeamAssessmentAuthService.ReadTeamAssessment);
                if (personHasGlobalTeamAssessmentRead) {
                    return true;
                }

                const teams = await teamsService.promiseToGetActiveTeamsForCurrentPerson();
                for (const team of teams) {
                    const canViewTeamAssessment = await authorisationService.promiseToGetHasAccess(TeamAssessmentAuthService.ReadTeamAssessment, team);
                    if (canViewTeamAssessment) {
                        return true;
                    }
                }

                return false;
            };
        }
    }

    public isFeatureActive(entity?: IEntityWithOptionalTeam) {
        return this.featuresService.isFeatureActive(FeatureName.CulturalNetworkTeamAssessment, entity);
    }

    public hasReadAccessToSurveys$(entity?: IEntityWithOptionalTeam) {
        return from(this.authService.promiseToGetHasAccess(TeamAssessmentAuthService.ReadTeamAssessment, entity));
    }

    public hasEditAccessToSurveys$(entity?: IEntityWithOptionalTeam) {
        return from(this.authService.promiseToGetHasAccess(TeamAssessmentAuthService.EditTeamAssessment, entity));
    }
}
